/**
 * Users Management Page
 *
 * CRUD for users within institution (ADMIN) or all (SUPER_ADMIN)
 */

import { useState } from 'react'
import {
  Users,
  Plus,
  Search,
  Power,
  PowerOff,
  Trash2,
  Edit,
  Key,
  Loader2,
} from 'lucide-react'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Select } from '@/presentation/components/ui/Select'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent } from '@/presentation/components/ui/Card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/presentation/components/ui/Table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/presentation/components/ui/Dialog'
import {
  useUsers,
  useActivateUser,
  useDeactivateUser,
  useDeleteUser,
} from '@/application/users/useUsers'
import { useInstitutions } from '@/application/institutions'
import { useAuth } from '@/application/auth'
import { toast } from '@/lib/toast'
import { UserFormModal } from '@/presentation/components/admin/UserFormModal'
import { ResetPasswordModal } from '@/presentation/components/admin/ResetPasswordModal'
import { USER_ROLES, type UserRole } from '@/config/constants'
import type { UserWithInstitution, UserSearchFilters } from '@/domain/user/types'

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Administrador',
  RADIOLOGIST: 'Radiólogo',
  DOCTOR: 'Médico',
  TECHNICIAN: 'Técnico',
  TRANSCRIPTOR: 'Transcriptor',
  PATIENT: 'Paciente',
}

const ROLE_OPTIONS = [
  { value: '', label: 'Todos los roles' },
  { value: 'ADMIN', label: 'Administrador' },
  { value: 'RADIOLOGIST', label: 'Radiólogo' },
  { value: 'DOCTOR', label: 'Médico' },
  { value: 'TECHNICIAN', label: 'Técnico' },
  { value: 'TRANSCRIPTOR', label: 'Transcriptor' },
]

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'active', label: 'Activos' },
  { value: 'inactive', label: 'Inactivos' },
]

export default function UsersPage() {
  const { user: currentUser } = useAuth()
  const isSuperAdmin = currentUser?.role === USER_ROLES.SUPER_ADMIN
  const isAdmin = currentUser?.role === USER_ROLES.ADMIN
  // Only ADMIN and SUPER_ADMIN can manage users (update, delete, reset password)
  const canManageUsers = isSuperAdmin || isAdmin
  const [filters] = useState<UserSearchFilters>({
    limit: 50,
  })
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('')
  const [statusFilter, setStatusFilter] = useState('')
  const [institutionFilter, setInstitutionFilter] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingUser, setEditingUser] = useState<UserWithInstitution | null>(null)
  const [resetPasswordUser, setResetPasswordUser] = useState<UserWithInstitution | null>(null)
  const [deletingUser, setDeletingUser] = useState<UserWithInstitution | null>(null)

  // Load institutions for SUPER_ADMIN filter
  const { data: institutionsData } = useInstitutions({ limit: 100 })
  const institutionOptions = [
    { value: '', label: 'Todas las instituciones' },
    ...(institutionsData?.institutions.map((inst) => ({
      value: inst.id,
      label: inst.name,
    })) || []),
  ]

  // Create a map of institution IDs to names for display
  const institutionMap = new Map(
    institutionsData?.institutions.map((inst) => [inst.id, inst.name]) || []
  )

  const { data, isLoading } = useUsers({
    ...filters,
    search: search || undefined,
    role: roleFilter || undefined as UserRole | undefined,
    isActive: statusFilter === '' ? undefined : statusFilter === 'active',
    institutionId: institutionFilter || undefined,
  })

  const activateMutation = useActivateUser()
  const deactivateMutation = useDeactivateUser()
  const deleteMutation = useDeleteUser()

  const handleToggleActive = (user: UserWithInstitution) => {
    if (user.isActive) {
      deactivateMutation.mutate(user.id, {
        onSuccess: () => toast.success('Usuario desactivado'),
        onError: () => toast.error('Error al desactivar usuario'),
      })
    } else {
      activateMutation.mutate(user.id, {
        onSuccess: () => toast.success('Usuario activado'),
        onError: () => toast.error('Error al activar usuario'),
      })
    }
  }

  const handleDelete = () => {
    if (deletingUser) {
      deleteMutation.mutate(deletingUser.id, {
        onSuccess: () => {
          toast.success('Usuario eliminado')
          setDeletingUser(null)
        },
        onError: () => toast.error('Error al eliminar usuario'),
      })
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Usuarios</h1>
          <p className="text-[var(--foreground-muted)]">
            {isSuperAdmin
              ? 'Gestión de usuarios de todas las instituciones'
              : 'Gestión de usuarios de tu institución'}
          </p>
        </div>
        {canManageUsers && (
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Usuario
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px] max-w-sm">
              <Input
                placeholder="Buscar por email..."
                value={search}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                leftIcon={<Search className="h-4 w-4" />}
              />
            </div>

            <div className="w-[180px]">
              <Select
                options={ROLE_OPTIONS}
                value={roleFilter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setRoleFilter(e.target.value as UserRole | '')}
              />
            </div>

            <div className="w-[150px]">
              <Select
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setStatusFilter(e.target.value)}
              />
            </div>

            {/* Institution filter - only for SUPER_ADMIN */}
            {isSuperAdmin && (
              <div className="w-[200px]">
                <Select
                  options={institutionOptions}
                  value={institutionFilter}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setInstitutionFilter(e.target.value)}
                />
              </div>
            )}

            <div className="text-sm text-[var(--foreground-muted)] ml-auto">
              {data?.total ?? 0} usuarios
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rol</TableHead>
                  {isSuperAdmin && <TableHead>Institución</TableHead>}
                  <TableHead>Estado</TableHead>
                  <TableHead>Último Login</TableHead>
                  <TableHead>Creado</TableHead>
                  <TableHead className="w-[180px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isSuperAdmin ? 8 : 7} className="text-center py-8">
                      <Users className="mx-auto h-12 w-12 text-[var(--foreground-muted)] opacity-50" />
                      <p className="mt-2 text-[var(--foreground-muted)]">
                        No hay usuarios
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  data?.users.map((user) => {
                    // Build full name from firstName and lastName
                    const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || null
                    return (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {fullName || <span className="text-[var(--foreground-muted)]">-</span>}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge variant={user.role === 'SUPER_ADMIN' ? 'destructive' : user.role === 'ADMIN' ? 'default' : 'secondary'}>
                          {ROLE_LABELS[user.role] || user.role}
                        </Badge>
                      </TableCell>
                      {isSuperAdmin && (
                        <TableCell className="text-[var(--foreground-muted)]">
                          {user.institutionId
                            ? institutionMap.get(user.institutionId) || 'Desconocida'
                            : <span className="text-amber-600 dark:text-amber-400">Sin institución</span>}
                        </TableCell>
                      )}
                      <TableCell>
                        <Badge variant={user.isActive ? 'default' : 'secondary'}>
                          {user.isActive ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[var(--foreground-muted)]">
                        {user.lastLoginAt
                          ? new Date(user.lastLoginAt).toLocaleDateString('es-CO')
                          : 'Nunca'}
                      </TableCell>
                      <TableCell className="text-[var(--foreground-muted)]">
                        {new Date(user.createdAt).toLocaleDateString('es-CO')}
                      </TableCell>
                      <TableCell>
                        {canManageUsers ? (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingUser(user)}
                              disabled={user.role === 'SUPER_ADMIN'}
                              title="Editar"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setResetPasswordUser(user)}
                              title="Cambiar contraseña"
                            >
                              <Key className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleActive(user)}
                              disabled={user.id === currentUser?.id || user.role === 'SUPER_ADMIN'}
                              title={user.isActive ? 'Desactivar' : 'Activar'}
                            >
                              {user.isActive ? (
                                <PowerOff className="h-4 w-4" />
                              ) : (
                                <Power className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeletingUser(user)}
                              disabled={user.id === currentUser?.id || user.role === 'SUPER_ADMIN'}
                              title="Eliminar"
                              className="text-[var(--destructive)] hover:text-[var(--destructive)]"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-sm text-[var(--foreground-muted)]">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <UserFormModal
        open={showCreateModal || !!editingUser}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setShowCreateModal(false)
            setEditingUser(null)
          }
        }}
        user={editingUser}
      />

      {/* Reset Password Modal */}
      <ResetPasswordModal
        open={!!resetPasswordUser}
        onOpenChange={(open: boolean) => !open && setResetPasswordUser(null)}
        user={resetPasswordUser}
      />

      {/* Delete Confirmation */}
      <Dialog
        open={!!deletingUser}
        onOpenChange={(open: boolean) => !open && setDeletingUser(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Eliminar usuario</DialogTitle>
            <DialogDescription>
              ¿Está seguro que desea eliminar al usuario{' '}
              <strong>{deletingUser?.email}</strong>? Esta acción no se puede
              deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDeletingUser(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
